// scheduling/components/distribution/DailyHoursGrid.jsx
// Controlled per-day hours input grid: one row per ISO week, one editable cell
// per weekday, with capacity-aware coloring. Lifted from the SchedulerGrid edit
// modal (lines 4397-4497) so it can be reused by the PlaceholderAssignModal and
// any future surface that needs the same input UX.
//
// Pure controlled component: caller owns `dailyHours` state. We render inputs,
// surface onChange events with the updated map, and never reach into the
// algorithm itself — the optimizer runs externally and feeds results in via
// dailyHours.
//
// The original inline JSX in SchedulerGrid is still intact (intentional — no
// edit/DND modal regression risk this round). A future refactor will switch
// those call sites to import this component.

import { useState } from 'react';

function toISO(d) { return d instanceof Date ? d.toISOString().slice(0, 10) : (d || '').slice(0, 10); }

export default function DailyHoursGrid({
  staffId,
  candidateDates,         // ISO[] — the days to render
  dailyHours,             // { iso: number } — current values (controlled)
  origDailyHours = {},    // { iso: number } — for edit-mode old→new strikethrough; {} for new entries
  onChange,               // (newDailyHours) => void
  getDayCap,              // (staffId, iso) => number
  entries = [],           // raw entries array
  excludeEntryIds,        // Set<EntryId> — entries to subtract from "existing" (edit-mode self)
  collapsedDefault = false,
  showTotal = true,
}) {
  const [showGrid, setShowGrid] = useState(!collapsedDefault);

  const isEdit = Object.keys(origDailyHours).length > 0;
  const exclSet = excludeEntryIds instanceof Set ? excludeEntryIds : new Set();

  // Collect all dates from candidateDates + any extras already present in dailyHours
  const allIsos = [...new Set([
    ...Object.keys(dailyHours).filter(iso => dailyHours[iso] != null),
    ...candidateDates,
  ])].sort();

  // Saturday handling: only show Sat column if any day in the range is a Saturday with capacity
  const hasSat = allIsos.some(iso => {
    const dt = new Date(iso + 'T12:00:00');
    return dt.getDay() === 6 && (staffId != null ? getDayCap(staffId, iso) > 0 : false);
  });
  const DOW = hasSat ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  // Group into week rows
  const weekGroups = [];
  let currentWeek = null;
  for (const iso of allIsos) {
    const dt = new Date(iso + 'T12:00:00');
    if (dt.getDay() === 0) continue; // skip Sundays
    const mon = new Date(dt); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
    const monKey = toISO(mon);
    if (!currentWeek || currentWeek.monKey !== monKey) {
      currentWeek = { monKey, monLabel: mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), days: {} };
      weekGroups.push(currentWeek);
    }
    const dowIdx = (dt.getDay() + 6) % 7;
    currentWeek.days[dowIdx] = iso;
  }

  const totalHours = Object.values(dailyHours).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const daysWithHours = Object.values(dailyHours).filter(v => v > 0).length;

  const handleDayChange = (iso, val) => {
    const next = { ...dailyHours, [iso]: val === '' ? '' : parseFloat(val) };
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <button type="button" onClick={() => setShowGrid(p => !p)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: 11, fontWeight: 700, color: '#6B7280', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ transform: showGrid ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
        Daily Hours ({daysWithHours} day{daysWithHours === 1 ? '' : 's'}, {Math.round(totalHours * 100) / 100}h total)
      </button>
      {showGrid && (
        <div className="sched-expand-enter" style={{ marginBottom: 8 }}>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${DOW.length}, 1fr)`, gap: 4, marginBottom: 4 }}>
            <div />
            {DOW.map(d => (
              <div key={d} style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'center' }}>{d}</div>
            ))}
          </div>
          {/* Week rows */}
          {weekGroups.map((wk, wi) => (
            <div key={wi} style={{ display: 'grid', gridTemplateColumns: `60px repeat(${DOW.length}, 1fr)`, gap: 4, marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, display: 'flex', alignItems: 'center' }}>{wk.monLabel}</div>
              {DOW.map((_, di) => {
                const iso = wk.days[di];
                if (!iso) return <div key={di} />;
                const hrs = parseFloat(dailyHours[iso]) || 0;
                const dayCap = staffId != null ? getDayCap(staffId, iso) : 8;
                const existingOther = entries.filter(e =>
                  e.StaffId === staffId && (typeof e.EntryDate === 'string' ? e.EntryDate : '').slice(0, 10) === iso && !exclSet.has(e.EntryId)
                ).reduce((s, e) => s + (e.Hours || 0), 0);
                const dayTotal = existingOther + hrs;
                const isOver = dayTotal > dayCap && dayCap > 0;
                const oldH = isEdit ? origDailyHours[iso] : undefined;
                const isChanged = isEdit ? (oldH == null ? hrs > 0 : oldH !== hrs) : hrs > 0;
                return (
                  <div key={di} style={{ textAlign: 'center' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                      padding: '3px 2px', borderRadius: 4, minHeight: 28,
                      border: `1.5px solid ${isOver ? '#EF4444' : isChanged ? '#F59E0B' : '#E5E7EB'}`,
                      background: isOver ? '#FEF2F2' : !isChanged ? '#F9FAFB' : '#fff',
                    }}>
                      {isEdit && isChanged && oldH != null && (
                        <span style={{ fontSize: 10, color: '#9CA3AF', textDecoration: 'line-through' }}>{oldH}</span>
                      )}
                      {isEdit && isChanged && oldH != null && (
                        <span style={{ fontSize: 8, color: '#9CA3AF' }}>&rarr;</span>
                      )}
                      <input type="number" min="0" max="24" step="0.5"
                        value={dailyHours[iso] ?? ''}
                        onChange={e => handleDayChange(iso, e.target.value)}
                        style={{
                          width: isEdit && isChanged && oldH != null ? 40 : '100%', padding: '0 2px', borderRadius: 2, fontSize: 12,
                          textAlign: 'center', fontWeight: 700, border: 'none', outline: 'none',
                          background: 'transparent',
                          color: isOver ? '#DC2626' : !isChanged ? '#9CA3AF' : '#374151',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 8, color: isOver ? '#DC2626' : '#9CA3AF', marginTop: 1, fontWeight: isOver ? 600 : 400 }}>
                      {dayTotal % 1 === 0 ? dayTotal : Math.round(dayTotal * 100) / 100}/{dayCap}h
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {showTotal && (
            <div style={{ fontSize: 12, fontWeight: 700, color: '#003D4C', marginTop: 4 }}>
              Total: {Math.round(totalHours * 100) / 100}h
            </div>
          )}
        </div>
      )}
    </div>
  );
}
